import React, {useState, useEffect, useMemo} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthService, VehicleService, VehicleRepairService, trackActionLog } from "../../services";
import {
  Spinner,
  Breadcrumb,
  BreadcrumbItem,
  Tabs,
  Tab,
  Dropdown,
  Modal,
  Button,
  Form,
} from "react-bootstrap";
import { Bell, Columns, Download, Filter, PersonSquare, Plus } from "react-bootstrap-icons";
import { ManageTable, Export } from "../../shared/components";
import { FUEL_TYPE, FUEL_TYPE_TEXT, VEHICLE_TITLE, VEHICLE_TITLE_TEXT, SEATING_CAPACITY_OPTIONS, LIFT_EQUIPPED, LIFT_EQUIPPED_TEXT } from "../../shared";
import { readPageUiObject, writePageUiObject } from "../../shared/pageFilterPersistence";
import {
  buildVehicleReminders,
  formatMileageLabel,
  repairsByVehicleId,
  vehicleHasReminderAlert,
} from "./vehicleReminders";

const canExportVehicleBasicColumn = () => AuthService.canViewVehicleBasic();

const NUMERIC_VEHICLE_SORT_KEYS = new Set(['vehicle_number', 'capacity', 'mileage', 'year']);

const compareVehicleListField = (a, b, key) => {
  const va = a[key];
  const vb = b[key];
  if (va == null && vb == null) return 0;
  if (va == null || va === '') return 1;
  if (vb == null || vb === '') return -1;
  if (NUMERIC_VEHICLE_SORT_KEYS.has(key)) {
    const na = Number(va);
    const nb = Number(vb);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  }
  return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
};

const VehicleList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [tag, setTag] = useState('');
  const [sorting, setSorting] = useState({key: 'vehicle_number', order: 'asc'});
	const [filteredVehicles, setFilteredVehicles] = useState(vehicles);
  const [vehiclesTab, setVehiclesTab] = useState(() =>
    searchParams.get('tab') === 'archived' ? 'archivedVehicles' : 'activeVehicles'
  );
  const [showInactive, setShowInactive] = useState(
    () => searchParams.get('tab') === 'archived'
  );
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showMonthlyMaintenanceModal, setShowMonthlyMaintenanceModal] = useState(false);
  const [maintenanceMonth, setMaintenanceMonth] = useState('');
  const [maintenanceYear, setMaintenanceYear] = useState('');
  const [monthlyFormExporting, setMonthlyFormExporting] = useState(false);
  const [showYearlyMaintenanceModal, setShowYearlyMaintenanceModal] = useState(false);
  const [yearlyMaintenanceYear, setYearlyMaintenanceYear] = useState('');
  const [yearlyFormExporting, setYearlyFormExporting] = useState(false);
  const [allVehicleReportExporting, setAllVehicleReportExporting] = useState(false);
  const [allRepairReportsExporting, setAllRepairReportsExporting] = useState(false);
  const [vehicleRepairs, setVehicleRepairs] = useState([]);
  const [reminderVehicle, setReminderVehicle] = useState(null);
  const [reminderToComplete, setReminderToComplete] = useState(null);
  const [completingReminder, setCompletingReminder] = useState(false);
  const [seatingCapacityFilter, setSeatingCapacityFilter] = useState('');
  const [fuelTypeFilter, setFuelTypeFilter] = useState('');
  const [titleFilter, setTitleFilter] = useState('');
  const [liftEquippedFilter, setLiftEquippedFilter] = useState('');
  const [columns, setColumns] = useState([
    {
      key: 'vehicle_number',
      label: 'Vehicle Number',
      show: true
    },
    {
      key: 'tag',
      label: 'License Plate',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    },
    {
      key: 'capacity',
      label: 'Seating Capacity',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    },
    {
      key: 'responsible_driver',
      label: 'Responsible Driver',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    },
    {
      key: 'mileage',
      label: 'Mileage',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    },
    {
      key: 'make',
      label: 'Make',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    },
    {
      key: 'model',
      label: 'Model',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    },
    {
      key: 'year',
      label: 'Year',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    },
    {
      key: 'vin',
      label: 'VIN Number',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    },
    {
      key: 'gps_tag',
      label: 'GPS ID',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    },
    {
      key: 'ezpass',
      label: 'E-ZPass',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    },
    {
      key: 'has_lift_equip',
      label: 'Lift Equipped',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    },
    {
      key: 'fuel_type',
      label: 'Fuel Type',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    },
    {
      key: 'title',
      label: 'Title',
      show: true,
      canExportColumn: canExportVehicleBasicColumn
    }
  ]);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  useEffect(() => {
    const saved = readPageUiObject("vehicles_list");
    if (saved) {
      if (typeof saved.keyword === "string") setKeyword(saved.keyword);
      if (typeof saved.tag === "string") setTag(saved.tag);
      if (typeof saved.showInactive === "boolean") setShowInactive(saved.showInactive);
      // Keep vehicle list baseline ordering deterministic: Vehicle Number ascending.
      // Do not restore stale persisted column sort from prior sessions.
      if (typeof saved.seatingCapacityFilter === "string") setSeatingCapacityFilter(saved.seatingCapacityFilter);
      if (typeof saved.fuelTypeFilter === "string") setFuelTypeFilter(saved.fuelTypeFilter);
      if (typeof saved.titleFilter === "string") setTitleFilter(saved.titleFilter);
      if (typeof saved.liftEquippedFilter === "string") setLiftEquippedFilter(saved.liftEquippedFilter);
    }
    setFiltersHydrated(true);
  }, []);

  const tableFiltersSnapshot = useMemo(
    () => ({
      keyword,
      tag,
      showInactive,
      sorting,
      seatingCapacityFilter,
      fuelTypeFilter,
      titleFilter,
      liftEquippedFilter,
    }),
    [keyword, tag, showInactive, sorting, seatingCapacityFilter, fuelTypeFilter, titleFilter, liftEquippedFilter]
  );
  useEffect(() => {
    if (!filtersHydrated) return;
    const t = setTimeout(() => {
      writePageUiObject("vehicles_list", tableFiltersSnapshot);
    }, 400);
    return () => clearTimeout(t);
  }, [filtersHydrated, tableFiltersSnapshot]);

  useEffect(() => {
    if (!AuthService.canViewVechiles()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change an admin account to login.')
      AuthService.logout();
      navigate(`/login`);
    }
    VehicleService.getAll().then((data) =>
      setVehicles(
        [...(data.data || [])].sort((a, b) => compareVehicleListField(a, b, 'vehicle_number'))
      )
    );
    VehicleRepairService.getAll()
      .then((res) => setVehicleRepairs(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setVehicleRepairs([]));
  }, []);

  useEffect(() => {
    setVehiclesTab(showInactive ? 'archivedVehicles' : 'activeVehicles');
  }, [showInactive]);

  useEffect(() => {
    if (showInactive) {
      setFilteredVehicles(vehicles && vehicles.filter(item => 
        (item?.vehicle_number?.toString()?.startsWith(keyword.toLowerCase()) || 
        item?.tag?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.ezpass?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.gps_tag?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.responsible_driver?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.make?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.vehicle_model?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.year?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.vin?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.fuel_type?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.title?.toLowerCase()?.startsWith(keyword.toLowerCase())) &&
        item?.status?.toLowerCase() !== 'active'
      ).filter((item) => {
        if (seatingCapacityFilter && `${item?.capacity || ''}` !== `${seatingCapacityFilter}`) return false;
        if (fuelTypeFilter && `${item?.fuel_type || ''}` !== fuelTypeFilter) return false;
        if (titleFilter && `${item?.title || ''}` !== titleFilter) return false;
        if (liftEquippedFilter && `${item?.has_lift_equip}` !== liftEquippedFilter) return false;
        return true;
      }))
    } else {
      setFilteredVehicles(vehicles && vehicles.filter(item => 
        (item?.vehicle_number?.toString()?.startsWith(keyword.toLowerCase()) || 
        item?.tag?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.ezpass?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.gps_tag?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.responsible_driver?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.make?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.vehicle_model?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.year?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.vin?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.fuel_type?.toLowerCase()?.startsWith(keyword.toLowerCase()) ||
        item?.title?.toLowerCase()?.startsWith(keyword.toLowerCase())) &&
        item?.status?.toLowerCase() === 'active'
      ).filter((item) => {
        if (seatingCapacityFilter && `${item?.capacity || ''}` !== `${seatingCapacityFilter}`) return false;
        if (fuelTypeFilter && `${item?.fuel_type || ''}` !== fuelTypeFilter) return false;
        if (titleFilter && `${item?.title || ''}` !== titleFilter) return false;
        if (liftEquippedFilter && `${item?.has_lift_equip}` !== liftEquippedFilter) return false;
        return true;
      }))
    }
  }, [keyword, vehicles, showInactive, seatingCapacityFilter, fuelTypeFilter, titleFilter, liftEquippedFilter]);

  useEffect(() => {
    const newVehicles = [...vehicles];
    if (!sorting.key) {
      const defaultSorted = [...newVehicles].sort((a, b) =>
        compareVehicleListField(a, b, 'vehicle_number')
      );
      setVehicles(defaultSorted);
      return;
    }
    const sortedVehicles = [...newVehicles].sort((a, b) =>
      compareVehicleListField(a, b, sorting.key)
    );
    setVehicles(sorting.order === 'asc' ? sortedVehicles : [...sortedVehicles].reverse());
  }, [sorting]);
  
  const goToEdit = (id) => {
    navigate(`/vehicles/edit/${id}?redirect=list`)
  }


  const goToView = (id) => {
    navigate(`/vehicles/${id}`)
  }

  const repairsMap = useMemo(() => repairsByVehicleId(vehicleRepairs), [vehicleRepairs]);
  const reminderItems = reminderVehicle
    ? buildVehicleReminders(
        reminderVehicle,
        repairsMap.get(`${reminderVehicle.id || reminderVehicle._id || ""}`) || []
      )
    : [];

  const openVehicleReminder = (event, vehicle) => {
    event.preventDefault();
    event.stopPropagation();
    setReminderVehicle(vehicle);
  };

  const confirmCompleteReminder = async () => {
    if (!reminderVehicle || !reminderToComplete || completingReminder) return;
    const vehicleId = reminderVehicle.id || reminderVehicle._id;
    if (!vehicleId) return;
    const existing = Array.isArray(reminderVehicle.completed_reminder_keys)
      ? reminderVehicle.completed_reminder_keys
      : [];
    const completedKeys = existing.includes(reminderToComplete.id)
      ? existing
      : [...existing, reminderToComplete.id];
    setCompletingReminder(true);
    try {
      await VehicleService.updateVehicles(vehicleId, {
        completed_reminder_keys: completedKeys,
      });
      const nextVehicle = { ...reminderVehicle, completed_reminder_keys: completedKeys };
      setReminderVehicle(nextVehicle);
      setVehicles((prev) =>
        prev.map((vehicle) =>
          `${vehicle.id || vehicle._id}` === `${vehicleId}` ? nextVehicle : vehicle
        )
      );
      setReminderToComplete(null);
    } catch (error) {
      window.alert(error?.response?.data?.message || "Failed to complete this reminder.");
    } finally {
      setCompletingReminder(false);
    }
  };

  const goToCreateNew = () => {
    navigate(`/vehicles`)
  }

  const getSortingImg = (key) => {
    return sorting.key === key ? (sorting.order === 'asc' ? 'up_arrow' : 'down_arrow') : 'default';
  }

  const sortTableWithField = (key) => {
    let newSorting = {
      key,
      order: 'asc',
    }
    
    if (sorting.key === key && sorting.order === 'asc') {
      newSorting = {...newSorting, order: 'desc'};
    }
    setSorting(newSorting);
  }

  const showArchive = (value) => {
		setVehiclesTab(value);
		setShowInactive(value === 'archivedVehicles');
		const next = new URLSearchParams(searchParams);
		if (value === 'archivedVehicles') {
			next.set('tab', 'archived');
		} else {
			next.delete('tab');
		}
		setSearchParams(next, { replace: true });
		// Recover all filters
		setKeyword('');
    setTag('');
    setSeatingCapacityFilter('');
    setFuelTypeFilter('');
    setTitleFilter('');
    setLiftEquippedFilter('');
		setSorting({key: 'vehicle_number', order: 'asc'});
	}

  const clearAndCloseFilter = () => {
    setSeatingCapacityFilter('');
    setFuelTypeFilter('');
    setTitleFilter('');
    setLiftEquippedFilter('');
    setShowFilterDropdown(false);
  };

  const applyAndCloseFilter = () => {
    setShowFilterDropdown(false);
  };

  const currentCalendarYear = new Date().getFullYear();
  const maintenanceYearChoices = [];
  for (let y = currentCalendarYear; y >= 2000; y -= 1) {
    maintenanceYearChoices.push(y);
  }

  const yearlyMaintenanceYearChoices = [];
  for (let y = currentCalendarYear; y >= 2020; y -= 1) {
    yearlyMaintenanceYearChoices.push(y);
  }

  const MONTH_OPTIONS = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const openMonthlyMaintenanceModal = () => {
    setShowExportDropdown(false);
    setMaintenanceMonth('');
    setMaintenanceYear('');
    setShowMonthlyMaintenanceModal(true);
  };

  const openYearlyMaintenanceModal = () => {
    setShowExportDropdown(false);
    setYearlyMaintenanceYear('');
    setShowYearlyMaintenanceModal(true);
  };

  const exportMonthlyMaintenanceFormFile = async () => {
    const m = parseInt(maintenanceMonth, 10);
    const y = parseInt(maintenanceYear, 10);
    if (!Number.isFinite(m) || m < 1 || m > 12) {
      window.alert('Please select a month.');
      return;
    }
    if (!Number.isFinite(y) || y < 2000 || y > currentCalendarYear) {
      window.alert('Please select a year (2000–' + currentCalendarYear + ').');
      return;
    }
    trackActionLog({
      actionVerb: "Export",
      summary: "Tap export button (vehicle monthly maintenance form)",
      entityType: "export",
      entityId: "vehicle-monthly-maintenance",
    }).catch(() => {});
    setMonthlyFormExporting(true);
    try {
      const response = await VehicleService.exportMonthlyMaintenanceForm(m, y);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const mm = String(m).padStart(2, '0');
      link.setAttribute('download', `Vehicle_Maintenance_Monthly_${y}-${mm}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setShowMonthlyMaintenanceModal(false);
    } catch (error) {
      console.error('Monthly maintenance form export failed:', error);
      let message = 'Failed to export monthly maintenance form.';
      try {
        const data = error?.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          try {
            const parsed = JSON.parse(text);
            message = parsed.message || text;
          } catch {
            message = text || message;
          }
        } else if (error?.message) {
          message = error.message;
        }
      } catch {
        /* keep default */
      }
      window.alert(message);
    } finally {
      setMonthlyFormExporting(false);
    }
  };

  const exportYearlyMaintenanceFormFile = async () => {
    const y = parseInt(yearlyMaintenanceYear, 10);
    if (!Number.isFinite(y) || y < 2020 || y > currentCalendarYear) {
      window.alert('Please select a year (2020–' + currentCalendarYear + ').');
      return;
    }
    trackActionLog({
      actionVerb: "Export",
      summary: "Tap export button (vehicle yearly maintenance form)",
      entityType: "export",
      entityId: "vehicle-yearly-maintenance",
    }).catch(() => {});
    setYearlyFormExporting(true);
    try {
      const response = await VehicleService.exportYearlyMaintenanceForm(y);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `Vehicle_Maintenance_Yearly_${y}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setShowYearlyMaintenanceModal(false);
    } catch (error) {
      console.error('Yearly maintenance form export failed:', error);
      let message = 'Failed to export yearly maintenance form.';
      try {
        const data = error?.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          try {
            const parsed = JSON.parse(text);
            message = parsed.message || text;
          } catch {
            message = text || message;
          }
        } else if (error?.message) {
          message = error.message;
        }
      } catch {
        /* keep default */
      }
      window.alert(message);
    } finally {
      setYearlyFormExporting(false);
    }
  };

  const exportAllVehicleReportFile = async () => {
    setShowExportDropdown(false);
    trackActionLog({
      actionVerb: "Export",
      summary: "Tap export button (all vehicle report)",
      entityType: "export",
      entityId: "vehicle-all-report",
    }).catch(() => {});
    setAllVehicleReportExporting(true);
    try {
      const response = await VehicleService.exportAllVehicleReport();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'All_Vehicles_Report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('All vehicle report export failed:', error);
      let message = 'Failed to export all vehicle report.';
      try {
        const data = error?.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          try {
            const parsed = JSON.parse(text);
            message = parsed.message || text;
          } catch {
            message = text || message;
          }
        } else if (error?.message) {
          message = error.message;
        }
      } catch {
        /* keep default */
      }
      window.alert(message);
    } finally {
      setAllVehicleReportExporting(false);
    }
  };

  const exportAllRepairMaintenanceReportsZip = async () => {
    setShowExportDropdown(false);
    trackActionLog({
      actionVerb: "Export",
      summary: "Tap export button (all vehicle repair and maintenance reports zip)",
      entityType: "export",
      entityId: "vehicle-all-repair-maintenance-reports",
    }).catch(() => {});
    setAllRepairReportsExporting(true);
    try {
      const response = await VehicleService.exportAllRepairMaintenanceReports();
      const blob = new Blob([response.data], { type: 'application/zip' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'All_Vehicle_Repair_Maintenance_Reports.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('All vehicle repair and maintenance reports export failed:', error);
      let message = 'Failed to export all vehicle repair and maintenance reports.';
      try {
        const data = error?.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          try {
            const parsed = JSON.parse(text);
            message = parsed.message || text;
          } catch {
            message = text || message;
          }
        } else if (error?.message) {
          message = error.message;
        }
      } catch {
        /* keep default */
      }
      window.alert(message);
    } finally {
      setAllRepairReportsExporting(false);
    }
  };

  const customFilterMenu = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => (
      <div
        ref={ref}
        style={style}
        className={className}
        aria-labelledby={labeledBy}
      >
        <h6>Filter By</h6>
        <div className="app-main-content-fields-section margin-sm dropdown-container">
          <div className="me-4">
            <div className="field-label">Seating Capacity</div>
            <select value={seatingCapacityFilter} onChange={(e) => setSeatingCapacityFilter(e.currentTarget.value)}>
              <option value="">All</option>
              {SEATING_CAPACITY_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>
          <div className="me-4">
            <div className="field-label">Fuel Type</div>
            <select value={fuelTypeFilter} onChange={(e) => setFuelTypeFilter(e.currentTarget.value)}>
              <option value="">All</option>
              {Object.values(FUEL_TYPE).map((value) => (
                <option key={value} value={value}>{FUEL_TYPE_TEXT[value] || value}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="app-main-content-fields-section margin-sm dropdown-container">
          <div className="me-4">
            <div className="field-label">Title</div>
            <select value={titleFilter} onChange={(e) => setTitleFilter(e.currentTarget.value)}>
              <option value="">All</option>
              {Object.values(VEHICLE_TITLE).map((value) => (
                <option key={value} value={value}>{VEHICLE_TITLE_TEXT[value] || value}</option>
              ))}
            </select>
          </div>
          <div className="me-4">
            <div className="field-label">Lift Equipped</div>
            <select value={liftEquippedFilter} onChange={(e) => setLiftEquippedFilter(e.currentTarget.value)}>
              <option value="">All</option>
              {Object.values(LIFT_EQUIPPED).map((value) => (
                <option key={value} value={value}>{LIFT_EQUIPPED_TEXT[value] || value}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="list row">
          <div className="col-md-12">
            <button className="btn btn-default btn-sm float-right" onClick={clearAndCloseFilter}> Cancel </button>
            <button className="btn btn-primary btn-sm float-right" onClick={applyAndCloseFilter}> Filter </button>
          </div>
        </div>
      </div>
    )
  );


  const table = <div className="list row mb-4">
		<div className="col-md-12 sticky-table-scroll">
			<table className="personnel-info-table"> 
				<thead>
					<tr>
						<th className="th-index">No.</th>
            <th className="th-vehicle-reminder" aria-label="Reminders" />
            {
							columns.filter(col => col.show).map((column, index) => <th className="sortable-header" key={index}>
								{column.label}  <span className="float-right" onClick={() => sortTableWithField(column.key)}><img src={`/images/${getSortingImg(column.key)}.png`}></img></span>
							</th>)
						}
					</tr>
					
				</thead>
				<tbody>
					{
						filteredVehicles.map((vehicle, index) => <tr key={vehicle.id}>
              <td className="td-index">{index + 1}</td>
              <td className="td-vehicle-reminder">
                <button
                  type="button"
                  className="vehicle-reminder-bell-btn"
                  onClick={(e) => openVehicleReminder(e, vehicle)}
                  aria-label={`Reminders for vehicle ${vehicle?.vehicle_number || ""}`}
                >
                  <Bell size={18} />
                  {vehicleHasReminderAlert(
                    vehicle,
                    repairsMap.get(`${vehicle.id || vehicle._id || ""}`) || []
                  ) && <span className="vehicle-reminder-bell-dot" />}
                </button>
              </td>
              {columns.find(col => col.key === 'vehicle_number')?.show && <td>{AuthService.canViewVechiles() ? <button className="btn btn-link btn-sm" onClick={() => goToView(vehicle?.id)}>{vehicle?.vehicle_number}</button> :  vehicle?.vehicle_number }</td>}
							{columns.find(col => col.key === 'tag')?.show && <td>{vehicle?.tag}</td>}
							{columns.find(col => col.key === 'capacity')?.show && <td>{vehicle?.capacity}</td>}
              {columns.find(col => col.key === 'responsible_driver')?.show && <td>{vehicle?.responsible_driver}</td>}
							{columns.find(col => col.key === 'mileage')?.show && <td>{vehicle?.mileage}</td>}
              {columns.find(col => col.key === 'make')?.show && <td>{vehicle?.make}</td>}
							{columns.find(col => col.key === 'model')?.show && <td>{vehicle?.vehicle_model}</td>}
							{columns.find(col => col.key === 'year')?.show && <td>{vehicle?.year}</td>}
              {columns.find(col => col.key === 'vin')?.show && <td>{vehicle?.vin}</td>}
              {columns.find(col => col.key === 'gps_tag')?.show && <td>{vehicle?.gps_tag}</td>}
              {columns.find(col => col.key === 'ezpass')?.show && <td>{vehicle?.ezpass}</td>}
              {columns.find(col => col.key === 'has_lift_equip')?.show && <td>{vehicle?.has_lift_equip ? 'Yes' : 'No'}</td>}
              {columns.find(col => col.key === 'fuel_type')?.show && <td>{FUEL_TYPE_TEXT[vehicle?.fuel_type] || vehicle?.fuel_type}</td>}
              {columns.find(col => col.key === 'title')?.show && <td>{VEHICLE_TITLE_TEXT[vehicle?.title] || vehicle?.title}</td>}
						</tr>)
					}
				</tbody>
			</table>
		</div>
	</div>;


  
  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/trans-routes/dashboard">Transportation</Breadcrumb.Item>
          <Breadcrumb.Item active>
            Vehicle Information
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
           <h4>
             All Vehicles 
           </h4> 
        </div>
      </div>
      <div className="app-main-content-list-container list-page all-vehicles-list-page">
				<div className="app-main-content-list-func-container">
					<Tabs
						id="vehicles-tab"
						activeKey={vehiclesTab}
						onSelect={(k) => showArchive(k)}
					>
						<Tab eventKey="activeVehicles" title="Active Vehicles">
							{table}
						</Tab>
						<Tab eventKey="archivedVehicles" title="Archived Vehicles">
							{table}
						</Tab>
					</Tabs>
					<div className="list-func-panel">
						<input className="me-2 with-search-icon" type="text" placeholder="Search" value={keyword}  onChange={(e) => setKeyword(e.currentTarget.value)} />
						<ManageTable columns={columns} onColumnsChange={setColumns} />
            <Dropdown
              key={'filter-vehicles'}
              id="filter-vehicles"
              className="me-2"
              show={showFilterDropdown}
              onToggle={(isOpen) => setShowFilterDropdown(isOpen)}
              autoClose={false}
            > 
              <Dropdown.Toggle variant="outline-secondary">
                <Filter size={16} className="me-2"></Filter>Filter
              </Dropdown.Toggle>
              <Dropdown.Menu as={customFilterMenu}/> 
            </Dropdown>
						{AuthService.canAddVehicle() && <button className="btn btn-primary me-2" onClick={() => goToCreateNew()}><Plus size={16}></Plus>Add New Vehicle</button>}
						{AuthService.canExportVehicleReport() && (
							<Export
								columns={columns}
								data={filteredVehicles}
								filename="vehicles"
								actionLogPageName="All Vehicles"
								hideBuiltinExportFormats
								show={showExportDropdown}
								onToggle={(isOpen) => {
									if (isOpen) {
										setShowFilterDropdown(false);
									}
									setShowExportDropdown(isOpen);
								}}
								customActions={[
									{
										label: 'Vehicle maintenance form monthly',
										onClick: openMonthlyMaintenanceModal,
									},
									{
										label: 'Vehicle maintenance form yearly',
										onClick: openYearlyMaintenanceModal,
									},
									{
										label: 'All vehicle report',
										onClick: () => void exportAllVehicleReportFile(),
										disabled: allVehicleReportExporting,
									},
									{
										label: 'All Vehicle Repair and Maintenance Report',
										onClick: () => void exportAllRepairMaintenanceReportsZip(),
										disabled: allRepairReportsExporting,
									},
								]}
							/>
						)}
					</div>
				</div>
      </div>
      {/* <div className="list row mb-4">
        <div className="col-md-12">
          <div className="mb-4">Filter By Tag: <input type="text" value={tag} onChange={(e) => setTag(e.currentTarget.value)}/></div>
          <table className="personnel-info-table"> 
            <thead>
              <tr>
                <th>Vehicle Number</th>
                <th>Tag Number</th>
                <th>Mileage</th>
                <th>Status</th>
                <th>Seating Capacity</th>
                <th></th>
              </tr>
              
            </thead>
            <tbody>
              {
                vehicles && vehicles.filter((item)=> item?.tag.toLowerCase().includes(tag.toLowerCase()))?.sort((a, b) => parseInt(a.vehicle_number)-parseInt(b.vehicle_number)).map(vehicle => <tr key={vehicle.id}>
                  <td>{vehicle?.vehicle_number}</td>
                  <td>{vehicle?.tag}</td>
                  <td>{vehicle?.mileage}</td>
                  <td>{vehicle?.status}</td>
                  <td>{vehicle?.capacity}</td>
                  <td>
                    {AuthService.canAddOrEditVechiles() && <button className="btn btn-primary btn-sm me-2" onClick={() => goToEdit(vehicle?.id)}>Edit</button> }
                    {AuthService.canViewVechiles() && <button className="btn btn-default btn-sm me-2" onClick={() => goToView(vehicle?.id)}>View</button>}
                  </td>
                </tr>)
              }
            </tbody>
          </table>
           
        </div>
      </div> */}

      <Modal
        show={showMonthlyMaintenanceModal}
        onHide={() => {
          if (!monthlyFormExporting) {
            setShowMonthlyMaintenanceModal(false);
          }
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Vehicle maintenance form monthly</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Choose the calendar month and year for repair records (replacement date). Export includes all
            vehicles for this site.
          </p>
          <div className="row g-2">
            <div className="col-md-6">
              <Form.Label className="small mb-1">Month</Form.Label>
              <Form.Select
                value={maintenanceMonth}
                onChange={(e) => setMaintenanceMonth(e.target.value)}
                disabled={monthlyFormExporting}
              >
                <option value="">Select month…</option>
                {MONTH_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-6">
              <Form.Label className="small mb-1">Year</Form.Label>
              <Form.Select
                value={maintenanceYear}
                onChange={(e) => setMaintenanceYear(e.target.value)}
                disabled={monthlyFormExporting}
              >
                <option value="">Select year…</option>
                {maintenanceYearChoices.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
          {maintenanceMonth && maintenanceYear ? (
            <div className="mt-3">
              <Button
                variant="primary"
                size="sm"
                disabled={monthlyFormExporting}
                onClick={() => void exportMonthlyMaintenanceFormFile()}
              >
                {monthlyFormExporting ? 'Exporting…' : 'Export'}
              </Button>
            </div>
          ) : null}
        </Modal.Body>
      </Modal>

      <Modal
        show={showYearlyMaintenanceModal}
        onHide={() => {
          if (!yearlyFormExporting) {
            setShowYearlyMaintenanceModal(false);
          }
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Vehicle maintenance form yearly</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Choose the calendar year for repair records (replacement date). Export includes all vehicles
            for this site.
          </p>
          <div className="row g-2">
            <div className="col-md-6">
              <Form.Label className="small mb-1">Year</Form.Label>
              <Form.Select
                value={yearlyMaintenanceYear}
                onChange={(e) => setYearlyMaintenanceYear(e.target.value)}
                disabled={yearlyFormExporting}
              >
                <option value="">Select year…</option>
                {yearlyMaintenanceYearChoices.map((yr) => (
                  <option key={yr} value={String(yr)}>
                    {yr}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
          {yearlyMaintenanceYear ? (
            <div className="mt-3">
              <Button
                variant="primary"
                size="sm"
                disabled={yearlyFormExporting}
                onClick={() => void exportYearlyMaintenanceFormFile()}
              >
                {yearlyFormExporting ? 'Exporting…' : 'Export'}
              </Button>
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
      <Modal
        show={Boolean(reminderVehicle)}
        onHide={() => setReminderVehicle(null)}
        className="vehicle-reminder-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reminder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reminderItems.length === 0 ? (
            <div className="text-muted">No reminders for this vehicle.</div>
          ) : (
            reminderItems.map((item) => (
              <div
                key={item.id}
                className={`vehicle-reminder-section${item.exceeded ? " is-exceeded" : ""}`}
              >
                <div className="vehicle-reminder-section-head">
                  <div className="vehicle-reminder-section-title">{item.title}</div>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setReminderToComplete(item)}
                  >
                    Complete
                  </button>
                </div>
                {item.kind === "deadline" ? (
                  <div className="vehicle-reminder-meta">
                    <div>Deadline: {item.dateValue}</div>
                    <div>Remaining Days: {item.remainingDays}</div>
                  </div>
                ) : (
                  <div className="vehicle-reminder-meta">
                    <div>
                      Current Mileage:{" "}
                      {item.currentMileage != null
                        ? `${formatMileageLabel(item.currentMileage)} miles`
                        : "—"}
                    </div>
                    <div>
                      Next Reminder:{" "}
                      {item.nextMileage != null
                        ? `${formatMileageLabel(item.nextMileage)} miles`
                        : "—"}
                    </div>
                  </div>
                )}
                <div className="vehicle-reminder-status">
                  <span className="vehicle-reminder-status-dot" />
                  {item.statusText}
                </div>
              </div>
            ))
          )}
        </Modal.Body>
      </Modal>
      <Modal
        show={Boolean(reminderToComplete)}
        onHide={() => {
          if (!completingReminder) setReminderToComplete(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Complete Reminder?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to mark this reminder as complete? It will no longer appear in Reminders. This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setReminderToComplete(null)}
            disabled={completingReminder}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void confirmCompleteReminder()}
            disabled={completingReminder}
          >
            {completingReminder ? "Confirming…" : "Confirm"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
};

export default VehicleList;